import React, { useState, useEffect } from "react";
import MainDashboardLayout from "../../../../Layout/MainDashboardLayout";
import GRISidebar from "../../../../Layout/GRISidebar";
import styles from "../../../../styles/gri/general.module.css";
import { Box, Button, Alert, Snackbar } from "@mui/material";
import EastIcon from "@mui/icons-material/East";
import { put, get } from "../../../../utils/api";
import { useNavigate, useParams } from "react-router-dom";
import SectionHeading from "../../GRI300/GRI308/SectionHeading";
import CustomTextField from "../../GRI300/GRI308/CustomTextField";
import ReviewBox from "../../../../Components/Common/ReviewBox";

import { currentYear } from "../../../../utils/generateYearRanges";

const GRI416_2 = ({ hide }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [disabled, setDisabled] = useState(false);

  const [errors, setErrors] = useState({});
  const [snackOpen, setSnackOpen] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const [formData, setFormData] = useState({
    nonComplianceFinesPenalties: { percentage: "", remark: "" },
    fineOrPenalty: { percentage: "", remark: "" },
    resultingWaring: { percentage: "", remark: "" },
    voluntary: { percentage: "", remark: "" },
    noHealthSafetyNonCompliance: { percentage: "", remark: "" },
  });
  const [griReview, setGriReview] = useState(() => JSON.parse(localStorage.getItem("griReview")));

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await get(`/gri/get?type=GRI400&Id=${id}`);
        if (response?.data?.data?.year < currentYear - 1)
          setDisabled(true);
        const data =
          response.data.data.GRI400?.nonComplianceHealthSafetyIncidents || {};
        setFormData({
          nonComplianceFinesPenalties: {
            percentage: data.nonComplianceFinesPenalties?.percentage || "",
            remark: data.nonComplianceFinesPenalties?.remark || "",
          },
          fineOrPenalty: {
            percentage: data.fineOrPenalty?.percentage || "",
            remark: data.fineOrPenalty?.remark || "",
          },
          resultingWaring: {
            percentage: data.resultingWaring?.percentage || "",
            remark: data.resultingWaring?.remark || "",
          },
          voluntary: {
            percentage: data.voluntary?.percentage || "",
            remark: data.voluntary?.remark || "",
          },
          noHealthSafetyNonCompliance: {
            percentage: data.noHealthSafetyNonCompliance?.percentage || "",
            remark: data.noHealthSafetyNonCompliance?.remark || "",
          },
        });
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const handleStorage = () => {
      setGriReview(JSON.parse(localStorage.getItem("griReview")));
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  useEffect(() => {
    setGriReview(JSON.parse(localStorage.getItem("griReview")));
  }, []);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleNestedInputChange = (section, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));

    // Construct the correct error key
    const errorKey = `${section}.${field}`;

    // Remove error for the specific field if the value is valid
    setErrors((prev) => {
      if (value.trim()) {
        const { [errorKey]: removedError, ...rest } = prev; // Remove the specific error key
        return rest;
      }
      return prev;
    });
  };

  const validateForm = () => {
    let newErrors = {};
    let formIsValid = true;

    // List of fields to validate
    const fieldsToValidate = [
      //   "identificationAndImplementationProcedure",
      "nonComplianceFinesPenalties",
      "fineOrPenalty",
      "resultingWaring",
      "voluntary",
      "noHealthSafetyNonCompliance",
    ];

    // Loop through each field and apply validation
    fieldsToValidate.forEach((field) => {
      const percentage = formData[field].percentage;
      const remark = formData[field].remark;

      // if (!percentage) {
      //   newErrors[`${field}.percentage`] = "This field is required";
      //   formIsValid = false;
      // } else
      if (isNaN(percentage)) {
        newErrors[`${field}.percentage`] = "Please enter a valid numeric value";
        formIsValid = false;
      } else if (percentage < 0 || percentage > 100) {
        newErrors[`${field}.percentage`] =
          "Please enter a value between 0 and 100";
        formIsValid = false;
      }

      // if (!remark) {
      //   newErrors[`${field}.remark`] = "This field is required";
      //   formIsValid = false;
      // }
    });

    setErrors(newErrors);
    return formIsValid;
  };

  const handleSubmit = async () => {
    if (!disabled) {
      if (!validateForm()) return; // Validate form before submission

      const payload = {
        type: "GRI400",
        submitted416_2: true,
        nonComplianceHealthSafetyIncidents: {
          ...formData,
        },
      };

      try {
        const token = sessionStorage.getItem("token");
        if (!token) {
          throw new Error("Authentication token is missing.");
        }
        await put(`/gri/update?Id=${id}`, payload);
        navigate(`/gri/gri400/417-1/${id}`);
      } catch (error) {
        setSnackOpen({
          open: true,
          message: "Error updating data. Please try again.",
          severity: "error",
        });
      }
    } else {
      navigate(`/gri/gri400/417-1/${id}`);
    }
  };

  const renderContent = ({ hide }) => (
    <>
      {!hide && (
        <p className={styles.section_heading}>416 : Customer Health & Safety</p>
      )}
      <p
        className={styles.main_heading}
        style={{ marginBottom: hide ? "-1rem" : "0" }}
      >
        416-2 : Incidents of non-compliance concerning the health and safety
        impacts of products and services
      </p>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        {/* Section A */}

        <Box>
          <SectionHeading text="a. How many incidents of non-compliance with regulations and/or voluntary codes regarding the health and safety impacts of products and services occurred within the reporting period? Please provide the following breakdown:" />
          <Box
            sx={{
              display: "flex",
              width: "100%",
              gap: "10px",
              alignItems: "center",
            }}
          >
            <Box sx={{ width: "15%" }}>
              <CustomTextField
                value={formData.nonComplianceFinesPenalties.percentage}
                onChange={(e) =>
                  handleNestedInputChange(
                    "nonComplianceFinesPenalties",
                    "percentage",
                    e.target.value
                  )
                }
                placeholder={"%"}
                error={!!errors["nonComplianceFinesPenalties.percentage"]}
                helperText={errors["nonComplianceFinesPenalties.percentage"]}
                disabled={disabled}
              />
            </Box>

            <Box sx={{ width: "85%" }}>
              <CustomTextField
                value={formData.nonComplianceFinesPenalties.remark}
                onChange={(e) =>
                  handleNestedInputChange(
                    "nonComplianceFinesPenalties",
                    "remark",
                    e.target.value
                  )
                }
                error={!!errors["nonComplianceFinesPenalties.remark"]}
                helperText={errors["nonComplianceFinesPenalties.remark"]}
                disabled={disabled}
              />
            </Box>
          </Box>

          <Box sx={{ marginLeft: "1rem" }}>
            <SectionHeading text="i. Incidents of non-compliance with regulations resulting in a fine or penalty." />
            <Box
              sx={{
                display: "flex",
                width: "100%",
                gap: "10px",
                alignItems: "center",
              }}
            >
              <Box sx={{ width: "15%" }}>
                <CustomTextField
                  value={formData.fineOrPenalty.percentage}
                  onChange={(e) =>
                    handleNestedInputChange(
                      "fineOrPenalty",
                      "percentage",
                      e.target.value
                    )
                  }
                  placeholder={"%"}
                  error={!!errors["fineOrPenalty.percentage"]}
                  helperText={errors["fineOrPenalty.percentage"]}
                  disabled={disabled}
                />
              </Box>

              <Box sx={{ width: "85%" }}>
                <CustomTextField
                  value={formData.fineOrPenalty.remark}
                  onChange={(e) =>
                    handleNestedInputChange(
                      "fineOrPenalty",
                      "remark",
                      e.target.value
                    )
                  }
                  error={!!errors["fineOrPenalty.remark"]}
                  helperText={errors["fineOrPenalty.remark"]}
                  disabled={disabled}
                />
              </Box>
            </Box>

            <SectionHeading text="ii. Incidents of non-compliance with regulations resulting in a warning." />
            <Box
              sx={{
                display: "flex",
                width: "100%",
                gap: "10px",
                alignItems: "center",
              }}
            >
              <Box sx={{ width: "15%" }}>
                <CustomTextField
                  value={formData.resultingWaring.percentage}
                  onChange={(e) =>
                    handleNestedInputChange(
                      "resultingWaring",
                      "percentage",
                      e.target.value
                    )
                  }
                  placeholder={"%"}
                  error={!!errors["resultingWaring.percentage"]}
                  helperText={errors["resultingWaring.percentage"]}
                  disabled={disabled}
                />
              </Box>

              <Box sx={{ width: "85%" }}>
                <CustomTextField
                  value={formData.resultingWaring.remark}
                  onChange={(e) =>
                    handleNestedInputChange(
                      "resultingWaring",
                      "remark",
                      e.target.value
                    )
                  }
                  error={!!errors["resultingWaring.remark"]}
                  helperText={errors["resultingWaring.remark"]}
                  disabled={disabled}
                />
              </Box>
            </Box>

            <SectionHeading text="iii.  Incidents of non-compliance with voluntary codes." />
            <Box
              sx={{
                display: "flex",
                width: "100%",
                gap: "10px",
                alignItems: "center",
              }}
            >
              <Box sx={{ width: "15%" }}>
                <CustomTextField
                  value={formData.voluntary.percentage}
                  onChange={(e) =>
                    handleNestedInputChange(
                      "voluntary",
                      "percentage",
                      e.target.value
                    )
                  }
                  placeholder={"%"}
                  error={!!errors["voluntary.percentage"]}
                  helperText={errors["voluntary.percentage"]}
                  disabled={disabled}
                />
              </Box>

              <Box sx={{ width: "85%" }}>
                <CustomTextField
                  value={formData.voluntary.remark}
                  onChange={(e) =>
                    handleNestedInputChange(
                      "voluntary",
                      "remark",
                      e.target.value
                    )
                  }
                  error={!!errors["voluntary.remark"]}
                  helperText={errors["voluntary.remark"]}
                  disabled={disabled}
                />
              </Box>
            </Box>
          </Box>

          <SectionHeading text="b. If the organization did not identify any instances of non-compliance with regulations and/or voluntary codes, a brief statement confirming this fact is sufficient." />
          <Box
            sx={{
              display: "flex",
              width: "100%",
              gap: "10px",
              alignItems: "center",
            }}
          >
            <Box sx={{ width: "15%" }}>
              <CustomTextField
                value={formData.noHealthSafetyNonCompliance.percentage}
                onChange={(e) =>
                  handleNestedInputChange(
                    "noHealthSafetyNonCompliance",
                    "percentage",
                    e.target.value
                  )
                }
                placeholder={"%"}
                error={!!errors["noHealthSafetyNonCompliance.percentage"]}
                helperText={errors["noHealthSafetyNonCompliance.percentage"]}
                disabled={disabled}
              />
            </Box>

            <Box sx={{ width: "85%" }}>
              <CustomTextField
                value={formData.noHealthSafetyNonCompliance.remark}
                onChange={(e) =>
                  handleNestedInputChange(
                    "noHealthSafetyNonCompliance",
                    "remark",
                    e.target.value
                  )
                }
                error={!!errors["noHealthSafetyNonCompliance.remark"]}
                helperText={errors["noHealthSafetyNonCompliance.remark"]}
                disabled={disabled}
              />
            </Box>
          </Box>
        </Box>

        {/* Submit Button */}

        {/* Snackbar for success/error messages */}
        <Snackbar
          open={snackOpen.open}
          message={snackOpen.message}
          autoHideDuration={3000}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          onClose={() => setSnackOpen({ ...snackOpen, open: false })}
        >
          <Alert
            severity={snackOpen.severity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {snackOpen.message}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );

  return (
    <>
      {!hide ? (
        <MainDashboardLayout sidebar={<GRISidebar />}>
          {renderContent({ hide })}

          <>
            {!griReview ? (
              <Box sx={{ ml: "1rem", width: "98%" }}>
                <ReviewBox url={"/gri"} handleSubmit={handleSubmit} />
              </Box>
            ) : (
              <Box textAlign="right" sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: "#1C486B",
                    textTransform: "none",
                    mt: 3,
                    mb: 5,
                  }}
                  onClick={handleSubmit}
                >
                  Save & Continue <EastIcon sx={{ ml: 2 }} />
                </Button>
              </Box>
            )}
          </>
        </MainDashboardLayout>
      ) : (
        renderContent({ hide })
      )}
    </>
  );
};

export default GRI416_2;
